import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
	clearFeedJumpTarget,
	feedSelector,
	setFeedPanelContextView,
	setFeedSelectedCardId,
} from "@/pages/Private/redux/feed/feed.slice";

import { feedCardKey } from "./feedCardKey";
import { scrollFeedCardIntoView } from "./scrollFeedCardIntoView";

/** How long the jumped-to card holds its wider ring before settling into the selection ring. */
const FLASH_MS = 900;

export interface FeedJumpToCompany {
	/** Goes on the sticky header — its measured height is the scroll offset. */
	headerRef: RefObject<HTMLDivElement>;
	/** `cardRef` factory for a rendered card, keyed by its `feedCardKey`. */
	registerCardRef: (cardKey: number) => (element: HTMLDivElement | null) => void;
	/** The card currently flashing after a jump, if any. */
	flashCardId: number | null;
}

/**
 * Consumes the pending jump target (D3): once the target page's cards are on screen, selects the
 * picked company's card — which is what points the docked panel at it — scrolls it below the
 * sticky header and flashes it.
 *
 * Lives outside `Feed.tsx` to keep the page shell under the 500-line cap, like the other
 * `Feed/utils` hooks.
 */
export const useFeedJumpToCompany = ({
	isFetching,
}: {
	isFetching: boolean;
}): FeedJumpToCompany => {
	const dispatch = useAppDispatch();
	const { cards, meta, page, jumpTargetCardId, jumpTargetContextView } =
		useAppSelector(feedSelector);

	const headerRef = useRef<HTMLDivElement>(null);
	const cardNodesRef = useRef(new Map<number, HTMLDivElement>());
	const refCallbacksRef = useRef(new Map<number, (element: HTMLDivElement | null) => void>());
	const [flashCardId, setFlashCardId] = useState<number | null>(null);

	// One stable callback per card key: a fresh closure per render would detach and
	// re-attach every card's ref on each render of the page.
	const registerCardRef = useCallback((cardKey: number) => {
		const existing = refCallbacksRef.current.get(cardKey);

		if (existing) {
			return existing;
		}

		const callback = (element: HTMLDivElement | null) => {
			if (element) {
				cardNodesRef.current.set(cardKey, element);
			} else {
				cardNodesRef.current.delete(cardKey);
			}
		};

		refCallbacksRef.current.set(cardKey, callback);

		return callback;
	}, []);

	useEffect(() => {
		if (jumpTargetCardId === null) {
			return;
		}

		// Wait for the cards of the target's own page. `isFetching` alone is not enough: on a
		// cross-page jump there is a render where the page has already moved but the refetch has
		// not started yet, and the old page's cards would look like a settled miss — spending the
		// target and killing the jump. `meta.page` is the page the on-screen cards came from.
		if (isFetching || meta?.page !== page) {
			return;
		}

		const targetCard = cards.find((card) => feedCardKey(card) === jumpTargetCardId);

		// A target that isn't here (filtered out, or a page from a stale companies query) has
		// nowhere to land — spend it anyway, so it can never fire on a later page.
		if (targetCard) {
			dispatch(setFeedSelectedCardId(jumpTargetCardId));

			// After the selection, never before: selecting a card resets the panel to the lead view.
			if (jumpTargetContextView) {
				dispatch(setFeedPanelContextView(jumpTargetContextView));
			}

			scrollFeedCardIntoView(cardNodesRef.current.get(jumpTargetCardId) ?? null, headerRef.current);
			setFlashCardId(jumpTargetCardId);
		}

		dispatch(clearFeedJumpTarget());
	}, [isFetching, jumpTargetCardId, jumpTargetContextView, cards, meta, page, dispatch]);

	useEffect(() => {
		if (flashCardId === null) {
			return;
		}

		const timer = setTimeout(() => setFlashCardId(null), FLASH_MS);

		return () => clearTimeout(timer);
	}, [flashCardId]);

	return { headerRef, registerCardRef, flashCardId };
};
